import type { EngineError, ResponseEnvelope } from '#layers/base/public';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOnboardingPopup } from '#layers/access/composables/use-onboarding-popup';
import { useSystemFetch } from '#layers/base/public';
import { useToastResultHandling } from '#layers/ui/public';

export interface InvitationPayload {
	email: string;
	orgRole?: string;
	workspace?: string;
	workspaceRoles?: string[];
}

export interface InvitationResult {
	email: string;
	id?: string;
	success: boolean;
	error?: EngineError;
}

function isPendingInvitationError(error: EngineError | undefined): boolean {
	return typeof error?.message === 'string'
		&& /invitation for this email already exists/i.test(error.message);
}

export function useInvitationsSubmit() {
	const systemFetch = useSystemFetch();
	const { t } = useI18n();
	const { onSuccess, onError } = useToastResultHandling();
	const { markAsDone } = useOnboardingPopup();
	const isSubmitting = ref(false);

	async function submit(invitations: InvitationPayload[]): Promise<{ succeeded: InvitationResult[]; failed: InvitationResult[] }> {
		if (invitations.length === 0) {
			return { succeeded: [], failed: [] };
		}

		isSubmitting.value = true;

		try {
			const { data: result } = await systemFetch<ResponseEnvelope<InvitationResult[]>>('/system/invites', {
				method: 'POST',
				body: invitations,
			});

			const failed = result.filter((r) => !r.success);
			const succeeded = result.filter((r) => r.success);

			if (failed.length > 0) {
				// prevent toast spam
				if (failed.length < 5) {
					for (const invitation of failed) {
						onError(invitation.error, {
							title: t('invites.error'),
							...(isPendingInvitationError(invitation.error)
								? { description: t('invites.pending_invitee_notice', { email: invitation.email }, 1) }
								: {}),
						});
					}
				}
				else {
					onError(failed.map((r) => r.error), {
						title: t('invites.error'),
					});
				}
			}

			if (succeeded.length > 0) {
				onSuccess({
					title: t('invites.success_invited', succeeded.length),
				});

				markAsDone('invitedTeam');
			}

			return { succeeded, failed };
		}
		catch (error) {
			onError(error, {
				title: t('invites.error'),
			});

			return { succeeded: [], failed: [] };
		}
		finally {
			isSubmitting.value = false;
		}
	}

	return { submit, isSubmitting };
}
